import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { COMPANY_LIST, setSelectedCompany, cacheDynamicCompanies } from "../companies";
import { Brand } from "../components/Brand";
import api from "../api";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, PlayCircle, Sparkles, UserCog, User, Loader2 } from "lucide-react";

const CompanySelectPage = () => {
  const navigate = useNavigate();
  const { user, loading, startDemo } = useAuth();
  const [dynamic, setDynamic] = useState([]);
  const [demoLoading, setDemoLoading] = useState("");

  useEffect(() => {
    if (!loading && user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    api.get("/companies/public").then((r) => {
      const list = r.data?.companies || [];
      setDynamic(list);
      cacheDynamicCompanies(list);
    }).catch(() => {});
  }, []);

  const choose = (code) => {
    setSelectedCompany(code);
    navigate("/login");
  };

  const launchDemo = async (role) => {
    setDemoLoading(role);
    const res = await startDemo(role);
    setDemoLoading("");
    if (res.ok) {
      toast.success("Modalità Demo avviata");
      navigate("/", { replace: true });
    } else {
      toast.error(res.error || "Impossibile avviare la demo");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#16345c] to-[#0d1b2f] text-white">
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        <div className="text-center mb-12 max-w-2xl">
          <Brand dark className="text-[34px]" />
          <p className="text-[15px] text-gray-300 mt-6 leading-relaxed text-center">
            Piattaforma risorse umane.<br />
            Seleziona per accedere.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 w-full max-w-3xl" data-testid="company-select-grid">
          {COMPANY_LIST.map((c) => (
            <button
              key={c.code}
              onClick={() => choose(c.code)}
              data-testid={`company-card-${c.code}`}
              className="group text-left bg-white/5 hover:bg-white/10 border border-white/15 hover:border-white/40 rounded-2xl p-7 transition-all duration-200 backdrop-blur-sm"
            >
              <span className="w-full h-[84px] rounded-xl bg-white flex items-center justify-center mb-5 px-6 shadow-sm overflow-hidden">
                {c.logo
                  ? <img src={c.logo} alt={c.label} className="max-h-12 max-w-full object-contain" />
                  : <span className="text-[24px] font-black text-center leading-tight" style={{ color: c.accent }}>{c.label}</span>}
              </span>
              <div className="text-[22px] font-black mb-1" style={{ color: "#fff" }}>{c.label}</div>
              <div className="text-[13.5px] text-gray-300 mb-6">{c.tagline}</div>
              <span className="inline-flex items-center gap-2 text-[13px] font-bold" style={{ color: c.accent }}>
                Accedi <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
          ))}
          {dynamic.map((c) => (
            <button
              key={c.code}
              onClick={() => choose(c.code)}
              data-testid={`company-card-${c.code}`}
              className="group text-left bg-white/5 hover:bg-white/10 border border-white/15 hover:border-white/40 rounded-2xl p-7 transition-all duration-200 backdrop-blur-sm"
            >
              <span className="w-full h-[84px] rounded-xl bg-white flex items-center justify-center mb-5 px-6 shadow-sm overflow-hidden">
                {c.has_logo
                  ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/companies/${c.code}/logo`} alt={c.label} className="max-h-14 max-w-full object-contain" />
                  : <span className="text-[30px] font-black" style={{ color: c.accent || "#0d6cb8" }}>{c.label}</span>}
              </span>
              <div className="text-[22px] font-black mb-1 text-white">{c.label}</div>
              <div className="text-[13.5px] text-gray-300 mb-6">{c.tagline || "Azienda del gruppo"}</div>
              <span className="inline-flex items-center gap-2 text-[13px] font-bold" style={{ color: c.accent || "#0d6cb8" }}>
                Accedi <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
          ))}
        </div>

        <div className="w-full max-w-3xl mt-10" data-testid="demo-section">
          <div className="rounded-2xl border border-[#ff7e00]/40 bg-[#ff7e00]/10 backdrop-blur-sm p-6 sm:p-7">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={18} className="text-[#ff7e00]" />
              <span className="text-[15px] font-black tracking-wide text-[#ffb066]">PROVA LA DEMO</span>
            </div>
            <p className="text-[13.5px] text-gray-300 leading-relaxed mb-5">
              Esplora il portale con dati fittizi già pronti (dipendenti, ferie, report, comunicazioni e dotazioni),
              senza registrazione. Ogni avvio riparte dagli stessi dati.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => launchDemo("admin")}
                disabled={!!demoLoading}
                data-testid="demo-start-admin"
                className="group flex items-center justify-center gap-2 rounded-xl bg-[#ff7e00] hover:bg-[#e56f00] disabled:opacity-60 text-white font-bold text-[14px] px-5 py-3.5 transition-colors"
              >
                {demoLoading === "admin" ? <Loader2 size={18} className="animate-spin" /> : <UserCog size={18} />}
                Prova come Admin HR
              </button>
              <button
                onClick={() => launchDemo("employee")}
                disabled={!!demoLoading}
                data-testid="demo-start-employee"
                className="group flex items-center justify-center gap-2 rounded-xl bg-white/10 hover:bg-white/20 border border-white/25 disabled:opacity-60 text-white font-bold text-[14px] px-5 py-3.5 transition-colors"
              >
                {demoLoading === "employee" ? <Loader2 size={18} className="animate-spin" /> : <User size={18} />}
                Prova come Dipendente
              </button>
            </div>
            <div className="mt-4 flex items-center gap-2 text-[12px] text-gray-400">
              <PlayCircle size={14} className="text-[#ff7e00]" />
              <span data-testid="demo-credentials">Credenziali demo: <b className="text-gray-200">demo@azienda.com</b> / <b className="text-gray-200">demo1234</b></span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 text-gray-400 text-[13px] mt-12">
          <ShieldCheck size={16} /> Accesso riservato e protetto
        </div>
      </div>
    </div>
  );
};

export default CompanySelectPage;
